/**
 * The sentence that goes under every figure computed from day rows.
 *
 * One component rather than four copies, because the four tiles must not be able to
 * drift into describing their sample differently — and because there is exactly one
 * rule about when it appears: **whenever any row in the window is `partial` or
 * `missing`, or any `(employee, date)` pair has not been computed.** No threshold,
 * no "only when it is bad enough". A threshold is a policy invented in a browser,
 * and the reader cannot see where it was set.
 *
 * `partial` and `missing` stay apart because `src/modules/aggregates/aggregate.ts`
 * keeps them apart: `missing` means the bucket was computed and nothing was
 * observed, which `openapi.yaml` says in as many words is **not** the claim that the
 * employee did nothing. Folding it into "partial" would lose the only distinction
 * that matters when somebody asks why a number is low.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { PartialData } from '../../states/PartialData';
import type { WindowFold } from '../../overview/fold';

export function hasGaps(fold: WindowFold): boolean {
  return fold.totals.partial > 0 || fold.totals.missing > 0 || fold.notComputedPairs > 0;
}

export interface CompletenessNoteProps {
  readonly fold: WindowFold;
  readonly testId: string;
  /** Extra prose this particular tile needs, rendered under the counts. */
  readonly children?: ReactNode;
}

export function CompletenessNote({ fold, testId, children }: CompletenessNoteProps): ReactNode {
  const { t } = useI18n();
  const counts = (
    <p className="wp-small" data-testid={`${testId}-counts`}>
      {t('overview.completeness', {
        complete: fold.totals.complete,
        partial: fold.totals.partial,
        missing: fold.totals.missing,
        notComputed: fold.notComputedPairs,
      })}
    </p>
  );

  if (!hasGaps(fold)) return counts;

  return (
    <>
      {counts}
      <PartialData excludes={t('overview.hours.excludes')} testId={`${testId}-partial`} />
      {children}
    </>
  );
}

/**
 * The window this figure actually covers, when the page was truncated.
 *
 * Rendered as its own sentence rather than folded into the completeness counts,
 * because it is a different kind of gap: the rows exist and were not fetched, where
 * `not computed` means they do not exist yet. A reader who cannot tell those apart
 * cannot tell whether waiting will help.
 */
export function TruncationNote({
  fold,
  limit,
  testId,
}: {
  readonly fold: WindowFold;
  readonly limit: number;
  readonly testId: string;
}): ReactNode {
  const { t } = useI18n();
  if (!fold.truncated || fold.covered === null) return null;
  return (
    <p className="wp-small wp-muted" data-testid={testId}>
      {t('overview.truncated', { limit, to: fold.covered.to })}
    </p>
  );
}
